import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, FileSpreadsheet, Building2 } from "lucide-react";
import { Button } from "@/components/ui/Button";

export function CentralizatorExport({ month }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!month) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/staff/reports/summary", { params: { month } });
        if (!alive) return;
        const list = (data?.projects || [])
          .map(p => ({ id: p.project_id, code: p.project_code, name: p.project_name, hours: p.hours, n: p.by_employee?.length || 0 }))
          .sort((a, b) => a.code.localeCompare(b.code));
        setProjects(list);
        setSelected(new Set(list.map(p => p.id)));
      } catch { if (alive) setProjects([]); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [month]);

  const allChecked = useMemo(() => projects.length > 0 && selected.size === projects.length, [projects, selected]);

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  async function download() {
    if (selected.size === 0) { toast.error("Bifează cel puțin un proiect"); return; }
    setBusy(true);
    try {
      const ids = [...selected].join(",");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/staff/reports/export/centralizator?month=${month}&project_ids=${encodeURIComponent(ids)}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try { msg = (await res.json()).detail || msg; } catch { /* ignore */ }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `centralizator-resurse-${month}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast.success("Centralizator Excel descărcat");
    } catch (e) {
      toast.error(`Export eșuat: ${e.message || e}`);
    } finally { setBusy(false); }
  }

  return (
    <div className="border border-brand/30 bg-brand/[0.03] rounded-xl p-5" data-testid="centralizator-card">
      <div className="flex items-start gap-4">
        <div className="h-11 w-11 rounded-lg flex items-center justify-center bg-brand/10 text-brand shrink-0">
          <Building2 className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[15px] font-medium text-ink">Centralizator resurse pe proiecte</div>
          <div className="text-[13px] text-ink-muted mt-0.5">
            Model client: foaia „Centralizator" + „Tarife" + câte o foaie per proiect bifat (rol, angajat, EUR/h, ore pontate, valoare).
            Tariful standard se preia din fișa angajatului; formulele rămân active în Excel.
          </div>

          <div className="mt-3 border border-neutral-200 bg-white rounded-lg">
            <div className="flex items-center justify-between px-3 py-2 border-b border-neutral-100">
              <label className="flex items-center gap-2 text-[13px] font-medium text-ink cursor-pointer">
                <input type="checkbox" checked={allChecked} disabled={projects.length === 0}
                  onChange={() => setSelected(allChecked ? new Set() : new Set(projects.map(p => p.id)))}
                  data-testid="centralizator-select-all" className="h-4 w-4 accent-brand" />
                Selectează toate proiectele
              </label>
              <span className="font-mono text-[11px] text-ink-muted" data-testid="centralizator-count">
                {selected.size}/{projects.length} bifate
              </span>
            </div>
            <div className="max-h-56 overflow-y-auto" data-testid="centralizator-project-list">
              {loading ? (
                <div className="flex items-center gap-2 px-3 py-4 text-sm text-ink-muted"><Loader2 className="h-4 w-4 animate-spin" /> Se încarcă proiectele…</div>
              ) : projects.length === 0 ? (
                <div className="px-3 py-4 text-sm text-ink-muted" data-testid="centralizator-empty">Nu există pontaje în luna selectată.</div>
              ) : projects.map(p => (
                <label key={p.id} className="flex items-center gap-3 px-3 py-2 hover:bg-neutral-50 cursor-pointer border-b border-neutral-50 last:border-0">
                  <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)}
                    data-testid={`centralizator-project-${p.id}`} className="h-4 w-4 accent-brand shrink-0" />
                  <span className="font-mono text-[12px] text-ink shrink-0">{p.code}</span>
                  <span className="text-[13px] text-ink truncate flex-1">{p.name}</span>
                  <span className="font-mono text-[11px] text-ink-muted shrink-0">{p.hours.toFixed(1)} h · {p.n} pers.</span>
                </label>
              ))}
            </div>
          </div>

          <div className="mt-3">
            <Button variant="primary" size="sm" icon={FileSpreadsheet} loading={busy}
              disabled={selected.size === 0} onClick={download} data-testid="centralizator-download">
              Descarcă Excel ({selected.size} {selected.size === 1 ? "proiect" : "proiecte"})
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
